import { type FC, useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, Chip, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ICON_MAP } from "@/shared/components/Player/timecodeIcons";

// Порт референсного VideoForm/Timecodes/IconSelect.js: автокомплит с
// группами, поиском по ключевым словам и «последними» иконками в localStorage.

const ICON_GROUPS: Record<string, string[]> = {
  general: [
    "PLAY_ARROW",
    "STAR",
    "FLAG",
    "TIMER",
    "TROPHY",
    "FIRE",
    "LIGHTNING",
    "BOLT",
    "AWESOME",
    "CELEBRATION",
    "TRENDING",
    "SPORTS",
  ],
  referee: ["WHISTLE", "RED_CARD", "YELLOW_CARD"],
  video: [
    "MOVIE",
    "VIDEOCAM",
    "CAMERA",
    "PLAY_CIRCLE",
    "REPLAY",
    "SLOW_MOTION",
    "SPEED",
    "VOLUME_UP",
    "FULLSCREEN",
    "MIC",
    "RECORD_VOICE",
  ],
  emotions: [
    "HEART",
    "THUMB_UP",
    "THUMB_DOWN",
    "EMOTIONS",
    "VERY_SATISFIED",
    "HOT_FIRE",
    "INSIGHTS",
  ],
  teamSports: [
    "FOOTBALL",
    "BASKETBALL",
    "SOCCER",
    "HOCKEY",
    "VOLLEYBALL",
    "BASEBALL",
    "RUGBY",
    "HANDBALL",
  ],
  individual: ["TENNIS", "GOLF", "RUNNING", "SWIMMING", "SKIING", "FITNESS"],
  combat: ["MMA", "KABADDI"],
  motor: ["MOTORSPORTS", "ESPORTS"],
};

const ICON_KEYWORDS: Record<string, string[]> = {
  PLAY_ARROW: ["play", "start", "begin", "воспроизведение", "старт", "начать"],
  STAR: ["star", "favorite", "best", "звезда", "избранное", "лучший"],
  FLAG: ["flag", "finish", "end", "флаг", "финиш", "конец"],
  TIMER: ["timer", "time", "clock", "таймер", "время", "часы"],
  TROPHY: ["trophy", "award", "win", "трофей", "награда", "победа"],
  FIRE: ["fire", "hot", "burning", "огонь", "горячий", "жгучий"],
  LIGHTNING: ["lightning", "fast", "speed", "молния", "быстро", "скорость"],
  BOLT: ["bolt", "power", "energy", "болт", "мощность", "энергия"],
  AWESOME: ["awesome", "great", "amazing", "потрясающий", "отличный"],
  CELEBRATION: ["celebration", "party", "joy", "празднование", "радость"],
  TRENDING: ["trending", "popular", "hot", "трендовый", "популярный"],
  SPORTS: ["sports", "game", "match", "спорт", "игра", "матч"],
  WHISTLE: ["whistle", "referee", "свисток", "судья"],
  RED_CARD: ["red", "card", "foul", "красная", "карточка", "нарушение"],
  YELLOW_CARD: ["yellow", "card", "warning", "желтая", "карточка"],
  MOVIE: ["movie", "film", "cinema", "фильм", "кино"],
  VIDEOCAM: ["video", "camera", "record", "видео", "камера", "запись"],
  CAMERA: ["camera", "photo", "picture", "камера", "фото", "картинка"],
  PLAY_CIRCLE: ["play", "circle", "воспроизведение", "круг"],
  REPLAY: ["replay", "repeat", "again", "повтор", "еще раз"],
  SLOW_MOTION: ["slow", "motion", "медленно", "движение"],
  SPEED: ["speed", "fast", "quick", "скорость", "быстро"],
  VOLUME_UP: ["volume", "sound", "audio", "громкость", "звук"],
  FULLSCREEN: ["fullscreen", "expand", "полный экран", "развернуть"],
  MIC: ["microphone", "mic", "audio", "микрофон", "звук"],
  RECORD_VOICE: ["record", "voice", "запись", "голос"],
  HEART: ["heart", "love", "like", "сердце", "любовь", "нравится"],
  THUMB_UP: ["thumb", "up", "like", "good", "палец", "вверх", "нравится"],
  THUMB_DOWN: ["thumb", "down", "dislike", "bad", "палец", "вниз", "плохо"],
  EMOTIONS: ["emotions", "feelings", "эмоции", "чувства"],
  VERY_SATISFIED: ["satisfied", "happy", "smile", "доволен", "улыбка"],
  HOT_FIRE: ["hot", "fire", "burning", "горячий", "огонь"],
  INSIGHTS: ["insights", "analytics", "data", "аналитика", "данные"],
  FOOTBALL: ["football", "american", "футбол", "американский"],
  BASKETBALL: ["basketball", "баскетбол"],
  SOCCER: ["soccer", "football", "футбол"],
  HOCKEY: ["hockey", "хоккей"],
  VOLLEYBALL: ["volleyball", "волейбол"],
  BASEBALL: ["baseball", "бейсбол"],
  RUGBY: ["rugby", "регби"],
  HANDBALL: ["handball", "гандбол"],
  TENNIS: ["tennis", "теннис"],
  GOLF: ["golf", "гольф"],
  RUNNING: ["running", "run", "marathon", "бег", "бежать", "марафон"],
  SWIMMING: ["swimming", "swim", "pool", "плавание", "бассейн"],
  SKIING: ["skiing", "ski", "snow", "лыжи", "снег"],
  FITNESS: ["fitness", "gym", "workout", "фитнес", "тренировка"],
  MMA: ["mma", "fight", "боевые искусства", "бой"],
  KABADDI: ["kabaddi", "кабадди"],
  MOTORSPORTS: ["motorsports", "racing", "car", "автоспорт", "гонки"],
  ESPORTS: ["esports", "gaming", "киберспорт", "игры"],
};

// Полные t()-ключи в мапе (не собираются шаблоном) — чтобы ключи находились
// грепом и saveMissing регистрировал их статически.
const GROUP_LABEL_KEYS: Record<string, string> = {
  general: "videos.timecodes.iconGroups.general",
  referee: "videos.timecodes.iconGroups.referee",
  video: "videos.timecodes.iconGroups.video",
  emotions: "videos.timecodes.iconGroups.emotions",
  teamSports: "videos.timecodes.iconGroups.teamSports",
  individual: "videos.timecodes.iconGroups.individual",
  combat: "videos.timecodes.iconGroups.combat",
  motor: "videos.timecodes.iconGroups.motor",
};

function getIconDisplayName(iconKey: string): string {
  return iconKey
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
}

interface IconOption {
  key: string;
  displayName: string;
  group: string;
  keywords: string[];
  component: (typeof ICON_MAP)[string] | undefined;
}

const RECENT_ICONS_KEY = "timecode-recent-icons";
const MAX_RECENT_ICONS = 5;

interface IconSelectProps {
  value: string;
  onChange: (icon: string) => void;
  label?: string;
  fullWidth?: boolean;
}

export const IconSelect: FC<IconSelectProps> = ({
  value,
  onChange,
  label,
  fullWidth = false,
}) => {
  const { t } = useTranslation();
  const [recentIcons, setRecentIcons] = useState<string[]>([]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_ICONS_KEY);
      if (stored) setRecentIcons(JSON.parse(stored) as string[]);
    } catch {
      // localStorage недоступен — «последние» просто не показываем
    }
  }, []);

  const saveToRecent = (iconKey: string) => {
    if (!iconKey) return;
    setRecentIcons((prev) => {
      const updated = [
        iconKey,
        ...prev.filter((icon) => icon !== iconKey),
      ].slice(0, MAX_RECENT_ICONS);
      try {
        localStorage.setItem(RECENT_ICONS_KEY, JSON.stringify(updated));
      } catch {
        // ignore
      }
      return updated;
    });
  };

  const allIcons = useMemo<IconOption[]>(() => {
    const icons: IconOption[] = [];
    Object.entries(ICON_GROUPS).forEach(([groupKey, iconKeys]) => {
      iconKeys.forEach((iconKey) => {
        icons.push({
          key: iconKey,
          displayName: getIconDisplayName(iconKey),
          group: GROUP_LABEL_KEYS[groupKey]
            ? t(GROUP_LABEL_KEYS[groupKey])
            : groupKey,
          keywords: ICON_KEYWORDS[iconKey] ?? [],
          component: ICON_MAP[iconKey],
        });
      });
    });
    return icons;
  }, [t]);

  const selectedIcon = allIcons.find((icon) => icon.key === value) ?? null;
  const SelectedIconComponent = selectedIcon?.component;

  return (
    <Box>
      <Autocomplete
        value={selectedIcon}
        onChange={(_event, newValue) => {
          if (newValue) {
            saveToRecent(newValue.key);
            onChange(newValue.key);
          }
        }}
        options={allIcons}
        filterOptions={(options, { inputValue }) => {
          if (!inputValue) return options;
          const searchTerm = inputValue.toLowerCase();
          return options.filter(
            (option) =>
              option.displayName.toLowerCase().includes(searchTerm) ||
              option.keywords.some((keyword) =>
                keyword.toLowerCase().includes(searchTerm)
              ) ||
              option.group.toLowerCase().includes(searchTerm)
          );
        }}
        groupBy={(option) => option.group}
        getOptionLabel={(option) => option.displayName}
        isOptionEqualToValue={(option, selected) => option.key === selected.key}
        popupIcon={null}
        clearIcon={null}
        blurOnSelect
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        fullWidth={fullWidth}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            fullWidth={fullWidth}
            InputProps={{
              ...params.InputProps,
              startAdornment: SelectedIconComponent && (
                <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
                  <SelectedIconComponent fontSize="small" />
                </Box>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          const OptionIcon = option.component;
          return (
            <Box
              key={key as string}
              component="li"
              {...optionProps}
              sx={{ display: "flex !important", alignItems: "center", gap: 1 }}
            >
              {OptionIcon && <OptionIcon fontSize="small" />}
              {option.displayName}
            </Box>
          );
        }}
      />
      {recentIcons.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="caption"
            color="textSecondary"
            display="block"
            sx={{ mb: 1 }}
          >
            {t("videos.timecodes.recentIcons")}
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
            {recentIcons.map((iconKey) => {
              const icon = allIcons.find((item) => item.key === iconKey);
              if (!icon) return null;
              const IconComponent = icon.component;
              return (
                <Chip
                  key={iconKey}
                  size="small"
                  variant={value === iconKey ? "filled" : "outlined"}
                  color={value === iconKey ? "primary" : "default"}
                  icon={
                    IconComponent ? (
                      <IconComponent fontSize="small" />
                    ) : undefined
                  }
                  label={icon.displayName}
                  onClick={() => {
                    saveToRecent(iconKey);
                    onChange(iconKey);
                  }}
                />
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};
